

































































import { Component, Prop, Vue, Watch } from "vue-property-decorator";
import { BODY_MAP_PANELS } from "@/constants/bodyMapPanels";

interface BodyMarker {
  view: string;
  type: "O" | "initials" | "yes";
  x: number; // 0..1
  y: number; // 0..1
  color?: string;
  note?: string;
}

@Component
export default class BodyMapCanvas extends Vue {
  @Prop({ default: () => [] }) value!: BodyMarker[];
  @Prop({ default: () => [] }) views!: Array<{ kode: string; label: string }>;
  @Prop({ default: false }) readonly!: boolean;
  @Prop({ default: "OP" }) initials!: string;
  @Prop({ default: "pria_anterior" }) initialView!: string;
  @Prop({ default: true }) showViewSelector!: boolean;
  @Prop({ default: true }) showModeToggle!: boolean;
  @Prop({ type: Number, default: 0 }) markRatio!: number;

  markers: BodyMarker[] = [];
  activeView = "pria_anterior";
  mode: "O" | "initials" | "yes" = "O";

  get activePanel() {
    return BODY_MAP_PANELS[this.activeView] || null;
  }

  get viewBox() {
    return this.activePanel
      ? { w: this.activePanel.w, h: this.activePanel.h }
      : { w: 240, h: 480 };
  }

  get canvasHeight() {
    const v = this.viewBox;
    const aspect = v.w / v.h;
    return Math.round(Math.max(240, Math.min(700, 560 / aspect)));
  }

  get baseMarkSize() {
    const v = this.viewBox;
    return Math.max(12, Math.round(Math.sqrt(v.w * v.h) * 0.055));
  }

  get markSize() {
    const rasio = Number(this.markRatio);
    if (rasio > 0) {
      return Math.max(12, Math.round(this.viewBox.h * rasio));
    }
    return this.baseMarkSize;
  }

  get strokeSize() {
    return Math.max(1.5, this.markSize * 0.13);
  }

  get dotSize() {
    return Math.max(2, Math.round(this.baseMarkSize * 0.14));
  }

  get viewOptions() {
    return this.views.length > 0 ? this.views : [
      { kode: "pria_anterior", label: "Tubuh Pria — Anterior" },
      { kode: "pria_posterior", label: "Tubuh Pria — Posterior" },
      { kode: "wanita_anterior", label: "Tubuh Wanita — Anterior" },
      { kode: "wanita_posterior", label: "Tubuh Wanita — Posterior" },
      { kode: "kepala_lateral_kiri", label: "Kepala — Lateral Kiri" },
      { kode: "kepala_lateral_kanan", label: "Kepala — Lateral Kanan" },
      { kode: "wajah_anterior", label: "Wajah — Anterior" },
      { kode: "kepala_posterior", label: "Kepala — Posterior" },
      { kode: "tangan_palmar_kiri", label: "Tangan Kiri — Palmar" },
      { kode: "tangan_palmar_kanan", label: "Tangan Kanan — Palmar" },
      { kode: "tangan_dorsal_kiri", label: "Tangan Kiri — Dorsal" },
      { kode: "tangan_dorsal_kanan", label: "Tangan Kanan — Dorsal" },
      { kode: "kaki_plantar_kiri", label: "Kaki Kiri — Plantar" },
      { kode: "kaki_plantar_kanan", label: "Kaki Kanan — Plantar" },
      { kode: "kaki_dorsal_kiri", label: "Kaki Kiri — Dorsal" },
      { kode: "kaki_dorsal_kanan", label: "Kaki Kanan — Dorsal" },
      { kode: "traktus_urinarius", label: "Traktus Urinarius (ginjal — ureter — buli)" },
      { kode: "genitalia_pria", label: "Genitalia Pria" },
      { kode: "odontogram_anak_atas", label: "Odontogram Anak — Rahang Atas" },
      { kode: "odontogram_anak_bawah", label: "Odontogram Anak — Rahang Bawah" },
      { kode: "odontogram_dewasa_atas", label: "Odontogram Dewasa — Rahang Atas" },
      { kode: "odontogram_dewasa_bawah", label: "Odontogram Dewasa — Rahang Bawah" },
    ];
  }

  get modeLabel() {
    return ({ O: 'Lingkaran "O"', initials: "Inisial Operator", yes: '"YES"' } as any)[this.mode];
  }

  get markersInActiveView() {
    return this.markers.filter(m => m.view === this.activeView);
  }

  mounted() {
    this.markers = Array.isArray(this.value) ? JSON.parse(JSON.stringify(this.value)) : [];
    if (this.initialView) this.activeView = this.initialView;
  }

  @Watch("value", { deep: true })
  onValueChange(val: BodyMarker[]) {
    this.markers = Array.isArray(val) ? JSON.parse(JSON.stringify(val)) : [];
  }

  handleClick(ev: MouseEvent) {
    if (this.readonly) return;
    const svg = this.$refs.svgEl as SVGSVGElement;
    if (!svg) return;
    const rect = svg.getBoundingClientRect();
    const pt = svg.createSVGPoint();
    pt.x = ev.clientX;
    pt.y = ev.clientY;
    const ctm = svg.getScreenCTM();
    if (!ctm) return;
    const local = pt.matrixTransform(ctm.inverse());
    const x = Math.max(0, Math.min(1, local.x / this.viewBox.w));
    const y = Math.max(0, Math.min(1, local.y / this.viewBox.h));
    this.markers.push({ view: this.activeView, type: this.mode, x, y, color: "#c00" });
    this.$emit("input", this.markers);
  }

  clearActiveView() {
    this.markers = this.markers.filter(m => m.view !== this.activeView);
    this.$emit("input", this.markers);
  }

  async snapshotPng(): Promise<string | null> {
    const svg = this.$refs.svgEl as SVGSVGElement;
    if (!svg) return null;
    try {
      const xml = new XMLSerializer().serializeToString(svg);
      const svg64 = btoa(unescape(encodeURIComponent(xml)));
      const img = new Image();
      img.src = `data:image/svg+xml;base64,${svg64}`;
      await new Promise(res => (img.onload = res));
      const canvas = document.createElement("canvas");
      const v = this.viewBox;
      const scale = Math.min(2, 900 / Math.max(v.w, v.h));
      canvas.width = Math.round(v.w * scale);
      canvas.height = Math.round(v.h * scale);
      const ctx = canvas.getContext("2d");
      if (!ctx) return null;
      ctx.fillStyle = "#fff";
      ctx.fillRect(0, 0, canvas.width, canvas.height);
      ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
      return canvas.toDataURL("image/png");
    } catch {
      return null;
    }
  }
}
